'use client';

import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Button, IconButton, Grid } from '@mui/material';
import EditIcon from '@/letsgo/icons/Edit'; // Import EditIcon for the team profile button
import TeamMembersTable from '@/components/team/TeamMembersTable';
import InviteMemberModal from '@/components/team/InviteMemberModal';
import EditMemberRoleModal from '@/components/team/EditMemberRoleModal';
import RemoveMemberConfirmationModal from '@/components/team/RemoveMemberConfirmationModal';
import EditTeamProfileModal from '@/components/team/EditTeamProfileModal'; // Import the new edit team profile modal
import TeamActivityLog from '@/components/team/TeamActivityLog'; // Import the new activity log component
import CustomRoleManagement from '@/components/team/CustomRoleManagement'; // Import CustomRoleManagement
import TeamWorkspaceManagement from '@/components/team/TeamWorkspaceManagement'; // Import TeamWorkspaceManagement

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  joinedAt: string;
}

// Define a Team interface to hold team data including logoUrl and description
interface Team {
  id: string;
  name: string;
  logoUrl?: string;
  description?: string;
}

const TeamsPage = () => {
  const [team, setTeam] = useState<Team | null>(null); // State to hold team profile data
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState<boolean>(false);
  const [selectedMemberToEdit, setSelectedMemberToEdit] = useState<{ id: string; name: string; currentRole: string } | null>(null);
  const [isRemoveMemberModalOpen, setIsRemoveMemberModalOpen] = useState<boolean>(false);
  const [selectedMemberToRemove, setSelectedMemberToRemove] = useState<{ id: string; name: string } | null>(null);
  const [isEditTeamProfileModalOpen, setIsEditTeamProfileModalOpen] = useState<boolean>(false); // State for edit team profile modal
  const [activityLogs, setActivityLogs] = useState<any[]>([]); // State for activity logs

  // Refetch team data and members function - now callable from anywhere
  const fetchTeamData = async () => {
    try {
      // For now, let's assume a user is part of at most one team and we fetch that team's data
      // In a multi-team scenario, we'd need to select a team first.
      const teamResponse = await fetch('/api/teams'); // Assuming an API route to fetch user's team data
      if (!teamResponse.ok) {
        // Handle case where team is null (e.g., user not in a team) gracefully
        const errorData = await teamResponse.json();
        if (teamResponse.status === 200 && errorData.team === null) {
          setTeam(null);
          setMembers([]);
          setActivityLogs([]);
          setLoading(false);
          return;
        }
        throw new Error(errorData.message || `HTTP error! status: ${teamResponse.status}`);
      }
      const teamData = await teamResponse.json();
      setTeam(teamData.team); // Access .team from the response

      const membersResponse = await fetch('/api/teams/members');
      if (!membersResponse.ok) {
        throw new Error(`HTTP error! status: ${membersResponse.status}`);
      }
      const membersData = await membersResponse.json();
      setMembers(membersData);

      // Fetch activity logs
      const activityResponse = await fetch('/api/teams/activity');
      if (!activityResponse.ok) {
        throw new Error(`HTTP error! status: ${activityResponse.status}`);
      }
      const activityData = await activityResponse.json();
      setActivityLogs(activityData);

    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching team data or members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, []);

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
  };

  const handleInviteMember = async (email: string, role: string) => {
    try {
      const response = await fetch('/api/teams/invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      console.log(`Invitation sent successfully to ${email}`);
      fetchTeamData(); // Refetch all data to update the list
      handleCloseInviteModal();
      // TODO: Add success notification
    } catch (err: any) {
      console.error('Error sending invitation:', err);
      // TODO: Add error notification
    }
  };

  const handleOpenEditRoleModal = (member: { id: string; name: string; currentRole: string }) => {
    setSelectedMemberToEdit(member);
    setIsEditRoleModalOpen(true);
  };

  const handleCloseEditRoleModal = () => {
    setIsEditRoleModalOpen(false);
    setSelectedMemberToEdit(null);
  };

  const handleSaveMemberRole = async (memberId: string, newRole: string) => {
    try {
      const response = await fetch(`/api/teams/members/${memberId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newRole }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      console.log(`Role updated successfully for member ${memberId} to ${newRole}`);
      fetchTeamData(); // Refetch all data to update the list
      handleCloseEditRoleModal();
      // TODO: Add success notification
    } catch (err: any) {
      console.error('Error updating member role:', err);
      // TODO: Add error notification
    }
  };

  const handleOpenRemoveMemberModal = (member: { id: string; name: string }) => {
    setSelectedMemberToRemove(member);
    setIsRemoveMemberModalOpen(true);
  };

  const handleCloseRemoveMemberModal = () => {
    setIsRemoveMemberModalOpen(false);
    setSelectedMemberToRemove(null);
  };

  const handleConfirmRemoveMember = async (memberId: string) => {
    try {
      const response = await fetch(`/api/teams/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      console.log(`Member ${memberId} removed successfully.`);
      fetchTeamData(); // Refetch all data to update the list
      handleCloseRemoveMemberModal();
      // TODO: Add success notification
    } catch (err: any) {
      console.error('Error removing member:', err);
      // TODO: Add error notification
    }
  };

  const handleOpenEditTeamProfileModal = () => {
    setIsEditTeamProfileModalOpen(true);
  };

  const handleCloseEditTeamProfileModal = () => {
    setIsEditTeamProfileModalOpen(false);
  };

  const handleSaveTeamProfile = async (teamName: string, logoUrl: string, description: string) => {
    try {
      if (!team?.id) {
        throw new Error('Team ID is missing.');
      }
      const response = await fetch('/api/teams', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: team.id, name: teamName, logoUrl, description }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      console.log(`Team profile updated successfully for ${teamName}`);
      fetchTeamData(); // Refetch all data to update the UI
      handleCloseEditTeamProfileModal();
      // TODO: Add success notification
    } catch (err: any) {
      console.error('Error updating team profile:', err);
      // TODO: Add error notification
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Loading Team Data...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 4, md: 8 }, mb: { xs: 4, md: 8 }, px: { xs: 2, md: 3 } }}>
        <Typography variant="h3" component="h1" color="error" gutterBottom>
          Error: {error}
        </Typography>
        <Typography variant="h6" component="p" color="text.secondary">
          Failed to load team data. Please try again later.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 4, md: 8 }, mb: { xs: 4, md: 8 }, px: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0, flex: { md: '1 1 auto' } }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ mr: { xs: 0, sm: 2 }, mb: 0 }}>
            {team?.name || 'Team Management'}
          </Typography>
          {team && (
            <IconButton aria-label="edit team profile" onClick={handleOpenEditTeamProfileModal} sx={{ flexShrink: 0 }}>
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenInviteModal}
          sx={{ alignSelf: { xs: 'stretch', md: 'center' }, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
        >
          Invite New Member
        </Button>
      </Box>
      {team?.description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {team.description}
        </Typography>
      )}
      <Typography variant="h6" component="p" color="text.secondary" sx={{ mb: 4 }}>
        Manage your team members and collaboration settings.
      </Typography>

      <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
        <Grid item xs={12} lg={7}>
          <TeamMembersTable members={members} onEditRole={handleOpenEditRoleModal} onRemoveMember={handleOpenRemoveMemberModal} />
        </Grid>
        <Grid item xs={12} lg={5}>
          <TeamActivityLog activityLogs={activityLogs} />
        </Grid>
      </Grid>

      {/* New Custom Role Management Component - Render only if team exists */}
      {team && <CustomRoleManagement teamId={team.id} />}

      {/* New Shared Workspaces Management Component - Render only if team exists */}
      {team && <TeamWorkspaceManagement teamId={team.id} />}

      {/* Modals */}
      <InviteMemberModal
        open={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        onInvite={handleInviteMember}
      />
      <EditMemberRoleModal
        open={isEditRoleModalOpen}
        onClose={handleCloseEditRoleModal}
        onSave={handleSaveMemberRole}
        member={selectedMemberToEdit ? { ...selectedMemberToEdit, currentRole: selectedMemberToEdit.currentRole } : null}
      />
      <RemoveMemberConfirmationModal
        open={isRemoveMemberModalOpen}
        onClose={handleCloseRemoveMemberModal}
        onConfirm={handleConfirmRemoveMember}
        member={selectedMemberToRemove}
      />
      <EditTeamProfileModal
        open={isEditTeamProfileModalOpen}
        onClose={handleCloseEditTeamProfileModal}
        onSave={handleSaveTeamProfile}
        team={team}
      />
    </Container>
  );
};

export default TeamsPage;
